import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ErrorPanelService } from '../../../services/error-panel.service';
import { ActivePatientFieldsForAiAnalysis } from '../../../models/patient-model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, interval, Subscription } from 'rxjs';
import { takeUntil, startWith, switchMap, takeWhile } from 'rxjs/operators';
import { PatientOverviewService } from './patient-overview.service';
import {
  PatientOverviewPipelineState,
  PatientOverviewFields,
} from './patient-overview.model';
import { ClineaseButtonComponent } from '../../../shared/clinease-button/clinease-button.component';
import { SharedModule } from '../../../shared/shared.module';
import { aiPatient_patientOverview_infoText } from '../../../models/descriptions/info-texts/info-texts';
import { PatientAiAnalysisService } from '../patient-ai-analysis.service';

@Component({
  selector: 'app-patient-overview',
  standalone: true,
  templateUrl: './patient-overview.component.html',
  styleUrls: [
    './patient-overview.component.scss',
    '../patient-ai-analysis.component.scss',
  ],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTableModule,
    MatTooltipModule,
    ClineaseButtonComponent,
    SharedModule,
  ],
})
export class PatientOverviewComponent implements OnChanges, OnDestroy, OnInit {
  @Input() patient!: ActivePatientFieldsForAiAnalysis;
  patientOverviewPipelineState: PatientOverviewPipelineState =
    PatientOverviewPipelineState.NOT_READY_TO_START;
  patSummState: string = 'NOT_EXECUTED';
  patDmrState: string = 'NOT_EXECUTED';
  numberOfCompletedAnalyses: number = 0;
  numberOfPastAppointments: number = 0;

  isLoading: boolean = true;

  private destroy$ = new Subject<void>();
  private pollingSubscription: Subscription | null = null;

  showInfoPanel: boolean = false;
  infoTitle: string = aiPatient_patientOverview_infoText.title;
  infoText: string = aiPatient_patientOverview_infoText.text;

  constructor(
    private patientOverviewService: PatientOverviewService,
    private snackBar: MatSnackBar,
    private errorPanelService: ErrorPanelService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private patientAiAnalysisService: PatientAiAnalysisService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['patient'] &&
      changes['patient'].currentValue &&
      changes['patient'].currentValue.id
    ) {
      this.initializeComponent();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
  }

  ngOnInit() {
    this.initializeComponent();
    // Subscribe to appointment AI outputs deletions
    this.patientAiAnalysisService.appointmentAIOutputsDeleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getPatientOverviewFields();
      });
    // Subscribe to appointment AI outputs creations
    this.patientAiAnalysisService.appointmentAIOutputsCreated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getPatientOverviewFields();
      });
  }

  private initializeComponent() {
    if (this.patient && this.patient.id) {
      console.log('Initializing PatientOverviewComponent with patient data');
      this.getPatientOverviewFields();
    }
  }

  getPatientOverviewFields() {
    this.isLoading = true;
    if (!this.patient || !this.patient.id) {
      console.log('Patient data not fully loaded yet');
      return;
    }

    this.patientOverviewService
      .getPatientOverviewFields(this.patient.id)
      .subscribe({
        next: (response) => {
          this.updatePatientOverviewFields(response);
          this.startPolling();
        },
        error: (err) => {
          console.log('error', err);
          this.errorPanelService.showErrorSnackBar('Si è verificato un errore');
        },
      });
  }

  private updatePatientOverviewFields(response: PatientOverviewFields) {
    this.patientOverviewPipelineState =
      response.patient_overview_pipeline_state;
    this.patSummState = response.patsumm_state;
    this.patDmrState = response.patdmr_state;
    this.numberOfCompletedAnalyses = response.number_of_completed_analyses;
    this.numberOfPastAppointments = response.number_of_past_appointments;

    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private startPolling() {
    console.log('Starting polling');
    this.stopPolling(); // Ensure any existing subscription is cleared
    this.pollingSubscription = interval(5000)
      .pipe(
        startWith(0),
        takeUntil(this.destroy$),
        switchMap(() => {
          console.log('Polling for patient pipeline status');
          return this.patientOverviewService.getPatientOverviewFields(
            this.patient.id
          );
        }),
        takeWhile((response) => {
          // Continue polling if any state is in progress
          return (
            response.patsumm_state === 'IN_PROGRESS' ||
            response.patdmr_state === 'IN_PROGRESS' ||
            response.patient_overview_pipeline_state === 'IN_PROGRESS'
          );
        }, true)
      )
      .subscribe({
        next: (response) => {
          this.updatePatientOverviewFields(response);

          if (
            response.patsumm_state !== 'IN_PROGRESS' &&
            response.patdmr_state !== 'IN_PROGRESS' &&
            response.patient_overview_pipeline_state !== 'IN_PROGRESS'
          ) {
            console.log('No processes in progress, stopping polling');
            this.stopPolling();
          }
        },
        error: (err) => {
          console.error('Error polling patient pipeline status:', err);
          this.errorPanelService.showErrorSnackBar('Errore nella polling');
        },
        complete: () => {
          console.log('Polling completed');
        },
      });
  }

  private stopPolling() {
    if (this.pollingSubscription) {
      console.log('Stopping existing polling');
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  startPatientSummaryPipeline() {
    if (
      this.patientOverviewPipelineState === 'NOT_READY_TO_START' ||
      this.patientOverviewPipelineState === 'EXECUTED_UP_TO_DATE' ||
      !this.patient ||
      !this.patient.id
    ) {
      return;
    }

    this.patientOverviewService
      .startPatientPipeline(this.patient.id)
      .subscribe({
        next: (response) => {
          const message =
            this.numberOfCompletedAnalyses === 0
              ? 'Analisi Complessiva Correttamente Cancellata'
              : 'Analisi Complessiva Iniziata';

          this.snackBar.open(message, 'Chiudi', { duration: 3000 });
          this.getPatientOverviewFields();
        },
        error: (err) => {
          this.errorPanelService.showErrorSnackBar(err.error.detail);
        },
      });
  }

  stopPatientSummaryPipeline() {
    console.log('Stopping patient summary pipeline');
    if (!this.patient || !this.patient.id) {
      console.error('Patient data not available');
      return;
    }
    this.patientOverviewService
      .stopOrDeletePatientPipeline(this.patient.id)
      .subscribe({
        next: (response: any) => {
          this.snackBar.open('Analisi Complessiva interrotta', 'Chiudi', {
            duration: 3000,
          });
          this.getPatientOverviewFields();
        },
        error: (err: any) => {
          console.error('Error stopping patient summary pipeline:', err);
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'interruzione della generazione della sintesi"
          );
        },
      });
  }

  navigateToAiFeaturesPage(page?: string) {
    if (page) {
      this.router.navigate(['/pazienti', this.patient.id, 'funzioni-ai', page]);
    } else {
      this.router.navigate(['/pazienti', this.patient.id, 'funzioni-ai']);
    }
  }

  getPatientOverviewPipelineState(): string {
    switch (this.patientOverviewPipelineState) {
      case 'NOT_READY_TO_START':
        return 'Non è possibile generare la sintesi in quanto non hai ancora analizzato alcun appuntamento';
      case 'READY_TO_START':
        return 'Genera sintesi ';
      case 'EXECUTED_UP_TO_DATE':
        return 'La sintesi generata è aggiornata';
      case 'EXECUTED_NOT_UP_TO_DATE':
        return 'La sintesi generata non è aggiornata';
      case 'IN_PROGRESS':
        return 'In corso';
      default:
        return 'Stato sconosciuto';
    }
  }

  getIconClass(state: string): string {
    switch (state) {
      case 'NOT_EXECUTED':
        return 'icon-grey';
      case 'IN_PROGRESS':
        return 'icon-yellow pulsing';
      case 'EXECUTED':
        return 'icon-green-pointer';
      case 'ERROR':
        return 'icon-red';
      default:
        console.warn(`Unknown state: ${state}`); // Add this line for debugging
        return 'icon-grey';
    }
  }

  stopOrDeletePatientSummaryPipeline() {
    console.log('Stopping or deleting patient summary pipeline');
    if (!this.patient || !this.patient.id) {
      console.error('Patient data not available');
      return;
    }
    this.patientOverviewService
      .stopOrDeletePatientPipeline(this.patient.id)
      .subscribe({
        next: (response: any) => {
          this.snackBar.open('Operazione completata con successo', 'Chiudi', {
            duration: 3000,
          });
          this.getPatientOverviewFields();
        },
        error: (err: any) => {
          console.error(
            'Error stopping or deleting patient summary pipeline:',
            err
          );
          this.errorPanelService.showErrorSnackBar(
            "Errore durante l'operazione"
          );
        },
      });
  }

  getPipelineStateIcon(): string {
    switch (this.patientOverviewPipelineState) {
      case 'NOT_READY_TO_START':
        return 'error_outline';
      case 'READY_TO_START':
        return 'play_circle_outline';
      case 'EXECUTED_UP_TO_DATE':
        return 'check_circle';
      case 'EXECUTED_NOT_UP_TO_DATE':
        return 'update';
      case 'IN_PROGRESS':
        return 'hourglass_empty';
      default:
        return 'help_outline';
    }
  }

  toggleInfoPanel() {
    this.showInfoPanel = !this.showInfoPanel;
  }
}
